import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowLeft, X, CheckCircle, XCircle, BarChart3, HelpCircle, Headphones } from 'lucide-react';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdvisorPricing({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [isRenewalMode, setIsRenewalMode] = useState(false);
  const [renewalPlan, setRenewalPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchUsageData();
  }, []);

  useEffect(() => {
    // Check URL parameters whenever location changes
    const params = new URLSearchParams(location.search);
    
    // Check if this is a renewal request
    if (params.get('renew') === 'true') {
      setIsRenewalMode(true);
      setRenewalPlan(params.get('plan'));
    } else {
      // Clear renewal mode if no renew parameter
      setIsRenewalMode(false);
      setRenewalPlan(null);
    }

    // Check if redirected from expired trial (only show modal on first visit)
    if (params.get('expired') === 'true' && 
        params.get('firstTime') === 'true' && 
        usage?.subscription_status === 'expired') {
      setShowExpiredModal(true);
    }
  }, [location.search, location.pathname, usage]);

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem('token');
      const usageResp = await axios.get(`${API}/advisor/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(usageResp.data);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = async (tier) => {
    try {
      const token = localStorage.getItem('token');
      
      // Create payment order with billing cycle
      const orderResponse = await axios.post(
        `${API}/payment/create-order`,
        { 
          plan_tier: tier.tier,
          billing_cycle: billingCycle
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const { order_id, amount, currency, key_id, user_email, user_name, billing_cycle: cycle } = orderResponse.data;
      
      // Initialize Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "FinAI",
        description: `${tier.name} Plan - ${cycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription`,
        order_id: order_id,
        prefill: {
          name: user_name,
          email: user_email
        },
        theme: {
          color: "#3B82F6"
        },
        handler: async function (response) {
          // Payment successful - verify on backend
          try {
            const verifyResponse = await axios.post(
              `${API}/payment/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan_tier: tier.tier,
                billing_cycle: cycle
              },
              { headers: { Authorization: `Bearer ${token}` }}
            );
            
            const duration = cycle === 'yearly' ? '12 months' : '1 month';
            alert(`🎉 Payment Successful!\n\nYour ${tier.name} plan (${duration}) is now active. You can start using all features immediately!`);
            
            // Refresh usage data
            fetchUsageData();
            
            // Redirect to dashboard
            setTimeout(() => navigate('/advisor/dashboard'), 2000);
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment received but verification failed. Please contact support with your payment ID.');
          }
        },
        modal: {
          ondismiss: function() {
            console.log('Payment cancelled by user');
          }
        }
      };
      
      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.body.appendChild(script);
      } else {
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
      
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment. Please try again or contact support.');
    }
  };

  // Pricing tiers with both monthly and yearly prices
  const pricingTiers = [
    {
      name: 'Starter',
      priceMonthly: '749',
      priceYearly: '6,499',
      tier: 'starter',
      clients: 50,
      plans: 100,
      features: [
        '50 clients',
        '100 plans/month',
        'Unlimited PDF downloads',
        'Custom branding on PDFs',
        'No FinAI watermark',
        'Email support'
      ],
      color: 'teal'
    },
    {
      name: 'Professional',
      priceMonthly: '1,499',
      priceYearly: '12,999',
      tier: 'professional',
      clients: 100,
      plans: 250,
      popular: true,
      features: [
        '100 clients',
        '250 plans/month',
        'Unlimited PDF downloads',
        'Custom branding on PDFs',
        'No FinAI watermark',
        'Priority email support'
      ],
      color: 'orange'
    },
    {
      name: 'Growth',
      priceMonthly: '2,999',
      priceYearly: '25,999',
      tier: 'growth',
      clients: 200,
      plans: 600,
      features: [
        '200 clients',
        '600 plans/month',
        'Unlimited PDF downloads',
        'Custom branding on PDFs',
        'No FinAI watermark',
        'Priority support'
      ],
      color: 'teal'
    }
  ];

  const currentTier = usage?.plan_tier || 'professional';

  // Calculate savings percentage for yearly
  const calculateSavings = (monthly, yearly) => {
    const monthlyNum = parseInt(monthly.replace(/,/g, ''));
    const yearlyNum = parseInt(yearly.replace(/,/g, ''));
    const yearlyMonthlyEquivalent = monthlyNum * 12;
    const savings = ((yearlyMonthlyEquivalent - yearlyNum) / yearlyMonthlyEquivalent) * 100;
    return Math.round(savings);
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div data-testid="pricing-skeleton" className="max-w-5xl mx-auto animate-pulse">
          <div className="text-center mb-12">
            <div className="h-10 w-64 bg-gray-800/40 rounded-lg mx-auto mb-4" />
            <div className="h-5 w-96 bg-gray-800/30 rounded mx-auto mb-6" />
            <div className="h-8 w-48 bg-gray-800/30 rounded-full mx-auto" />
          </div>
          <div className="h-24 bg-gray-800/30 rounded-xl mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="h-80 bg-gray-800/30 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="plans-billing-page" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[450px] h-[450px] rounded-full bg-gradient-to-r from-orange-600/[0.06] to-amber-500/[0.06] blur-3xl" animate={{ x: [0, 30, 0], y: [0, -25, 0] }} transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }} style={{ top: '5%', right: '10%' }} />
        </div>
      )}
      
      {/* Expired Trial Modal */}
      {showExpiredModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowExpiredModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              🎉 Your Free Trial Has Ended
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Thank you for trying FinAI! Your 7-day trial has ended, but all your data is safe and accessible.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                📊 Your Usage During Trial:
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                • {usage?.clients_count || 0} clients created
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                • {usage?.total_plans || 0} plans generated
              </p>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose a plan below to continue creating plans for your clients.
            </p>
            
            <Button 
              className="w-full"
              onClick={() => setShowExpiredModal(false)}
            >
              Choose Your Plan
            </Button>
          </Card>
        </div>
      )}
      
      <motion.div className="max-w-7xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {/* Back Button */}
        <motion.div className="flex items-center justify-between mb-6" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Button
            variant="ghost"
            onClick={() => navigate('/advisor/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          {/* Show "Exit Renewal Mode" button when in renewal mode */}
          {isRenewalMode && (
            <Button
              variant="outline"
              onClick={() => {
                navigate('/advisor/pricing');
                setIsRenewalMode(false);
                setRenewalPlan(null);
              }}
              className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:border-orange-400 dark:text-orange-400"
            >
              View All Plans
            </Button>
          )}
        </motion.div>

        {/* Header */}
        <motion.div className="text-center mb-12" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            {isRenewalMode ? 'Renew Your Subscription' : <><span className="bg-gradient-to-r from-orange-500 to-amber-400 bg-clip-text text-transparent">Plans</span> & Billing</>}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {isRenewalMode 
              ? `Renew your ${renewalPlan ? renewalPlan.charAt(0).toUpperCase() + renewalPlan.slice(1) : ''} plan to continue without interruption.`
              : usage?.subscription_status === 'active'
              ? 'Manage your subscription and explore other plans.'
              : 'Select the plan that fits your practice. Upgrade or downgrade anytime.'}
          </p>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              Monthly
            </span>
            <button
              data-testid="billing-toggle"
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                billingCycle === 'yearly' ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                Save ~28%
              </span>
            )}
          </div>
          
          {usage?.subscription_status === 'trial' && (
            <div className="mt-4 inline-block bg-blue-100 dark:bg-blue-900/30 px-4 py-2 rounded-full">
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                🎁 Free trial ends {usage?.trial_end ? 
                  `${new Date(usage.trial_end).toLocaleDateString('en-IN', {day: 'numeric', month: 'short'})} (${Math.ceil((new Date(usage.trial_end) - new Date()) / (1000 * 60 * 60 * 24))} days left)` 
                  : ''}
              </p>
            </div>
          )}

          {/* Active Subscription Status */}
          {usage?.subscription_status === 'active' && usage?.subscription_end && !isRenewalMode && (
            <div className="mt-6">
              <Card className="max-w-2xl mx-auto p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-emerald-900 dark:text-emerald-200 font-semibold text-lg">
                          {usage?.plan_tier?.charAt(0).toUpperCase() + usage?.plan_tier?.slice(1)} Plan
                        </span>
                        <span className="px-2.5 py-0.5 bg-emerald-200 dark:bg-emerald-700 text-emerald-800 dark:text-emerald-100 text-sm font-medium rounded-full">
                          Active
                        </span>
                      </div>
                      <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
                        Renews on {new Date(usage.subscription_end).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                        <span className="mx-2">•</span>
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {Math.ceil((new Date(usage.subscription_end) - new Date()) / (1000 * 60 * 60 * 24))} days left
                        </span>
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
                    onClick={() => {
                      // Scroll down to show pricing cards
                      window.scrollTo({ top: 600, behavior: 'smooth' });
                    }}
                  >
                    View Plans
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* Expired Status Banner - Compact style like active */}
          {usage?.subscription_status === 'expired' && !isRenewalMode && (
            <div className="mt-6">
              <Card className="max-w-2xl mx-auto p-6 bg-gradient-to-r from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 border-rose-200 dark:border-rose-800">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-800 flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-rose-900 dark:text-rose-200 font-semibold text-lg">
                          {usage?.plan_tier && !['free_trial', 'trial'].includes(usage.plan_tier)
                            ? `${usage.plan_tier.charAt(0).toUpperCase() + usage.plan_tier.slice(1)} Plan`
                            : 'Free Trial'
                          }
                        </span>
                        <span className="px-2.5 py-0.5 bg-rose-200 dark:bg-rose-700 text-rose-800 dark:text-rose-100 text-sm font-medium rounded-full">
                          Expired
                        </span>
                      </div>
                      <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">
                        Expired on {new Date(usage?.subscription_end || usage?.trial_end).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                        <span className="mx-2">•</span>
                        <span className="text-rose-600 dark:text-rose-400">
                          Choose a plan to restore access
                        </span>
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={() => {
                      // Scroll down to show pricing cards
                      window.scrollTo({ top: 600, behavior: 'smooth' });
                    }}
                  >
                    Choose Plan
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </motion.div>

        {/* Current Usage Summary */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}>
        <Card className="p-6 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            {usage?.subscription_status === 'trial' ? 'Your Trial Usage' : 'Your Current Usage'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400">Clients: 
                <span className="font-bold text-gray-800 dark:text-gray-100 ml-2">
                  {usage?.clients_count || 0} / {usage?.subscription_status === 'expired' ? '—' : (usage?.client_limit || 0)}
                </span>
                {usage?.subscription_status === 'trial' && (
                  <span className="text-xs text-blue-600 ml-1">(trial limit)</span>
                )}
                {usage?.subscription_status === 'expired' && (
                  <span className="text-xs text-rose-500 ml-1">(expired)</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400">Plans This Month: 
                <span className="font-bold text-gray-800 dark:text-gray-100 ml-2">
                  {usage?.plans_this_month || 0} / {usage?.subscription_status === 'expired' ? '—' : (usage?.plan_limit || 0)}
                </span>
                {usage?.subscription_status === 'trial' && (
                  <span className="text-xs text-blue-600 ml-1">(trial limit)</span>
                )}
                {usage?.subscription_status === 'expired' && (
                  <span className="text-xs text-rose-500 ml-1">(expired)</span>
                )}
              </p>
            </div>
          </div>
        </Card>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-5xl mx-auto">
          {pricingTiers.map((tier, idx) => {
            // Show "CURRENT PLAN" if active OR expired paid subscription (not trial)
            const isTrialPlan = !usage?.plan_tier || ['free_trial', 'trial'].includes(usage.plan_tier);
            const isCurrentPlan = tier.tier === currentTier && 
                                  (usage?.subscription_status === 'active' || 
                                   (usage?.subscription_status === 'expired' && !isTrialPlan));
            const isPopular = tier.popular;
            const currentPrice = billingCycle === 'yearly' ? tier.priceYearly : tier.priceMonthly;
            const savings = calculateSavings(tier.priceMonthly, tier.priceYearly);
            
            return (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: isMobile ? 0 : 0.25 + idx * 0.1 }}
                whileHover={!isMobile ? { y: -6, transition: { duration: 0.25 } } : {}}
                className="group"
              >
              <Card 
                data-testid={`pricing-card-${tier.tier}`}
                className={`p-6 relative ${
                  isPopular 
                    ? 'border-orange-500 dark:border-orange-600 shadow-lg' 
                    : isCurrentPlan
                    ? 'border-blue-500 dark:border-blue-600'
                    : ''
                }`}
              >
                {isPopular && (
                  <div className={`absolute -top-3 ${isCurrentPlan ? 'left-4' : 'left-1/2 transform -translate-x-1/2'}`}>
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </span>
                  </div>
                )}

                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  {tier.name}
                </h3>
                
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                    ₹{currentPrice}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    /{billingCycle === 'yearly' ? 'year' : 'month'}
                  </span>
                  {billingCycle === 'yearly' && (
                    <div className="mt-1">
                      <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
                        ₹{(parseInt(tier.priceMonthly.replace(/,/g, '')) * 12).toLocaleString('en-IN')}
                      </span>
                      <span className="ml-2 text-sm font-semibold text-green-600 dark:text-green-400">
                        Save {savings}%
                      </span>
                    </div>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                      <CheckCircle2 className={`w-5 h-5 ${
                        tier.color === 'orange' ? 'text-orange-500' : 'text-teal-500'
                      }`} />
                      <span className={idx === 0 || idx === 1 ? 'font-semibold' : ''}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan && usage?.subscription_status === 'active' && !isRenewalMode ? (
                  (() => {
                    // Check if subscription is expiring soon (7 days or less)
                    const daysUntilExpiry = usage?.subscription_end 
                      ? Math.ceil((new Date(usage.subscription_end) - new Date()) / (1000 * 60 * 60 * 24))
                      : 999;
                    
                    // If expiring within 7 days, allow renewal
                    if (daysUntilExpiry <= 7) {
                      return (
                        <Button 
                          data-testid={`renew-btn-${tier.tier}`}
                          className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                          onClick={() => handlePlanSelection(tier)}
                        >
                          Renew {tier.name}
                        </Button>
                      );
                    }
                    
                    // Otherwise, show disabled "Current Plan"
                    return (
                      <Button 
                        variant="outline"
                        className="w-full"
                        disabled
                      >
                        Current Plan
                      </Button>
                    );
                  })()
                ) : (
                  <Button 
                    data-testid={`select-btn-${tier.tier}`}
                    className={`w-full ${
                      (isRenewalMode && isCurrentPlan) || 
                      (isCurrentPlan && (usage?.subscription_status === 'expired' && !isTrialPlan)) || 
                      isPopular 
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    onClick={() => {
                      // Always allow payment for all users (expired or trial)
                      handlePlanSelection(tier);
                    }}
                  >
                    {(() => {
                      const hasActivePlan = usage?.subscription_status === 'active';
                      const hasExpiredPlan = usage?.subscription_status === 'expired' && !isTrialPlan;
                      
                      // Expired plan - allow renewal
                      if (hasExpiredPlan && isCurrentPlan && !isRenewalMode) {
                        return `Renew ${tier.name}`;
                      }
                    // Renewal mode - current plan
                    if (isRenewalMode && isCurrentPlan) {
                      return `Renew ${tier.name} - ₹${currentPrice}`;
                    }
                    // Renewal mode - different plan
                    if (isRenewalMode && !isCurrentPlan) {
                      return `Switch to ${tier.name} - ₹${currentPrice}`;
                    }
                    // Active or expired paid user - different plan (upgrade/downgrade)
                    if ((hasActivePlan || hasExpiredPlan) && !isCurrentPlan && !isRenewalMode) {
                      const currentTierPrice = parseInt((pricingTiers.find(t => t.tier === currentTier)?.priceMonthly || '0').replace(/,/g, ''));
                      const thisTierPrice = parseInt(tier.priceMonthly.replace(/,/g, ''));
                      return thisTierPrice > currentTierPrice ? `Upgrade to ${tier.name}` : `Downgrade to ${tier.name}`;
                    }
                    // Trial or expired trial (never paid)
                    if (usage?.subscription_status === 'trial' || (usage?.subscription_status === 'expired' && isTrialPlan)) {
                      return 'Choose Plan';
                    }
                      return 'Select Plan';
                    })()}
                  </Button>
                )}
              </Card>
              </motion.div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.5 }}>
        <Card className="p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-orange-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                Can I change my plan anytime?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Yes! You can upgrade immediately for instant access to higher limits. Downgrades take effect at the next billing cycle.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                What happens if I exceed my limits?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                You'll be notified when approaching your limits. You can upgrade anytime to continue without interruption.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
                How do the client and plan limits work together?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                You can manage up to your client limit and generate up to your monthly plan limit. Both are enforced to ensure fair usage.
              </p>
            </div>
          </div>
        </Card>
        </motion.div>

        {/* Need Help Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: isMobile ? 0 : 0.55 }}>
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 text-center">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2 flex items-center justify-center gap-2">
            <Headphones className="w-5 h-5 text-blue-500" />
            Need help choosing a plan?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Our team is here to help you find the perfect plan for your practice.
          </p>
          <a href="mailto:support@finaigpt.com">
            <Button variant="outline">
              Contact Support
            </Button>
          </a>
        </Card>
        </motion.div>
      </motion.div>
      
      <Footer />
    </div>
  );
}
